import axiosInstance from './axiosInstance';

// 教师相关API
// 教师相关API对象
export const plasoApi = {
  /**
   * 获取教师列表
   * @param {string} searchStr 搜索关键词（可选）
   * @returns {Promise} 返回教师列表响应
   */
  getTeacherList: (searchStr = '') => {
    return axiosInstance.get(`plaso/teacher-list?search_str=${searchStr}`);
  },

  /**
   * 获取合并的教师列表（包括API和已删除教师）
   * @returns {Promise} 返回合并的教师列表响应
   */
  getCombinedTeachers: () => {
    return axiosInstance.get(`plaso/combined-teachers`);
  },

  /**
   * 获取已删除教师列表
   * @returns {Promise} 返回已删除教师列表响应
   */
  getDeletedTeachers: () => {
    const token = localStorage.getItem('token');
    return axiosInstance.get(`plaso/deleted-teachers`, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 添加已删除教师
   * @param {Object} data 包含teacher_id和teacher_name的对象
   * @returns {Promise} 返回添加结果响应
   */
  addDeletedTeacher: (data) => {
    const token = localStorage.getItem('token');
    return axiosInstance.post(`plaso/deleted-teachers`, data, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 删除已删除教师记录
   * @param {string} teacherId 教师ID
   * @returns {Promise} 返回删除结果响应
   */
  deleteDeletedTeacher: (teacherId) => {
    const token = localStorage.getItem('token');
    return axiosInstance.delete(`plaso/deleted-teachers/${teacherId}`, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 获取历史课堂信息
   * @param {string} teacherId 教师ID
   * @param {number} page 页码，从1开始
   * @param {number} pageSize 每页数量
   * @returns {Promise} 返回历史课堂信息响应
   */
  getHistoryClasses: (teacherId, page = 1, pageSize = 10) => {
    const token = localStorage.getItem('token');
    return axiosInstance.get(`plaso/history-classes?teacher_id=${teacherId}&page=${page}&page_size=${pageSize}`, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 获取存档任务列表
   * @param {number} page 页码
   * @param {number} pageSize 每页数量
   * @param {Object} filters 筛选条件
   * @returns {Promise} 返回存档任务列表响应
   */
  getDownloadTasks: (page = 1, pageSize = 10, filters = {}) => {
    const token = localStorage.getItem('token');
    
    // 确保页码参数正确
    if (page < 1) page = 1;
    if (pageSize < 1) pageSize = 10;
    
    const params = new URLSearchParams({
      page: page,
      page_size: pageSize,
    });

    // 添加筛选条件
    if (filters.grade) {
      params.append('grade_name', filters.grade);
    }
    if (filters.classType) {
      params.append('class_name', filters.classType);
    }
    if (filters.course) {
      params.append('course_name', filters.course);
    }
    if (filters.searchText) {
      params.append('search_text', filters.searchText);
    }
    if (filters.custom_tag) {
      params.append('custom_tag', filters.custom_tag);
    }
    if (filters.status !== undefined && filters.status !== null) {
      params.append('status', filters.status);
    }
    if (filters.status_in) {
      params.append('status_in', filters.status_in);
    }
    if (filters.status_exclude) {
      params.append('status_exclude', filters.status_exclude);
    }
    if (filters.sort_order) {
      params.append('sort_order', filters.sort_order);
    }
    
    // 构建完整URL
    const fullUrl = `plaso/download-tasks?${params}`;

    return axiosInstance.get(fullUrl, {
      headers: {
        'Authorization': token
      }
    }).catch(error => {
      console.error('获取存档任务列表失败:', error);
      throw error;
    });
  },

  /**
   * 创建存档任务
   * @param {Object} data 存档任务数据，包含teacher_id, teacher_name, history_classes等
   * @returns {Promise} 返回创建存档任务响应
   */
  createDownloadTask: (data) => {
    const token = localStorage.getItem('token');
    // 为批量任务创建设置更长的超时时间（6分钟）
    const taskCount = data.history_classes ? data.history_classes.length : 0;
    const timeout = Math.max(360000, taskCount * 10000); // 基础6分钟，每个任务额外10秒
    
    return axiosInstance.post(`plaso/create-download`, data, {
      headers: {
        'Authorization': token
      },
      timeout: timeout // 动态超时设置
    });
  },

  /**
   * 获取实时课堂列表
   * @param {string} teacherId 教师ID
   * @param {number} startTime 开始时间戳(秒)
   * @param {number} endTime 结束时间戳(秒)
   * @returns {Promise} 返回实时课堂列表响应
   */
  getLiveClasses: (teacherId, startTime, endTime) => {
    const token = localStorage.getItem('token');
    const requestData = {
      speakerId: teacherId,
      classStart: startTime,
      classEnd: endTime
    };
    
    return axiosInstance.post(`plaso/live-classes`, requestData, {
      headers: {
        'Authorization': token
      }
    }).then(response => {
      return response;
    }).catch(error => {
      console.error('【实时课堂错误】:', error.response || error);
      throw error;
    });
  },

  /**
   * 获取课堂回看地址
   * @param {number} classId 课堂ID
   * @returns {Promise} 返回课堂回看地址响应
   */
  getClassPlaybackUrl: (classId) => {
    const token = localStorage.getItem('token');
    const requestData = {
      classId: classId
    };
    
    return axiosInstance.post(`plaso/class-playback`, requestData, {
      headers: {
        'Authorization': token
      }
    }).then(response => {
      return response;
    }).catch(error => {
      console.error('【获取回看地址错误】:', error.response || error);
      throw error;
    });
  },

  /**
   * 删除存档任务
   * @param {number} id 任务ID
   * @returns {Promise} 返回删除任务响应
   */
  deleteDownloadTask: (id) => {
    const token = localStorage.getItem('token');
    return axiosInstance.delete(`plaso/download-tasks/${id}`, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 批量删除存档任务
   * @param {Array<number>} taskIds 任务数据库ID列表
   * @returns {Promise} 返回批量删除任务响应
   */
  batchDeleteDownloadTasks: (taskIds) => {
    const token = localStorage.getItem('token');
    return axiosInstance.post(`plaso/download-tasks/batch-delete`, { ids: taskIds }, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 查询视频存档任务状态
   * @param {string} taskId 任务ID
   * @returns {Promise} 返回任务状态响应
   */
  queryVideoTask: (taskId) => {
    const token = localStorage.getItem('token');
    return axiosInstance.get(`plaso/query-video-task?task_id=${taskId}`, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 获取带签名的下载URL
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回带签名的下载URL响应
   */
  getSignedDownloadUrl: (taskId) => {
    const token = localStorage.getItem('token');
    return axiosInstance.post(`plaso/get-signed-download-url`, { task_id: taskId }, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 获取带签名的强制下载URL
   * @param {number} taskId 任务ID
   * @returns {Promise} 返回带签名的强制下载URL响应
   */
  getSignedDownloadUrlForDownload: (taskId) => {
    const token = localStorage.getItem('token');
    return axiosInstance.post(`plaso/get-signed-download-url-for-download`, { task_id: taskId }, {
      headers: {
        'Authorization': token
      }
    });
  },

  /**
   * 获取届次列表
   * @returns {Promise} 返回届次列表响应
   */
  getSessionList: () => {
    return axiosInstance.get(`plaso/session-list`);
  },

  /**
   * 获取班次列表
   * @returns {Promise} 返回班次列表响应
   */
  getClassGroupList: () => {
    return axiosInstance.get(`plaso/class-group-list`);
  },

  /**
   * 获取班级列表（伯索平台）
   * @param {string} key 班级名称/教师名称模糊匹配，可选
   * @param {number} pageStart 页码，默认0
   * @param {number} pageSize 每页个数，默认50
   * @returns {Promise} 返回班级列表响应
   */
  getGroupList: (key = '', pageStart = 0, pageSize = 50) => {
    const params = new URLSearchParams();
    if (key) params.append('key', key);
    params.append('page_start', pageStart.toString());
    params.append('page_size', pageSize.toString());
    
    return axiosInstance.get(`plaso/group-list?${params.toString()}`);
  },

  /**
   * 获取课程列表
   * @returns {Promise} 返回课程列表响应
   */
  getCourseList: () => {
    return axiosInstance.get(`plaso/course-list`);
  },

  /**
   * 更新课堂归档信息
   * @param {number} taskId 任务ID
   * @param {Object} data 更新的数据，包含grade_name, class_name, course_name
   * @returns {Promise} 返回更新响应
   */
  updateCourseArchiveInfo: (taskId, data) => {
    const token = localStorage.getItem('token');
    return axiosInstance.put(`plaso/update-course-archive-info/${taskId}`, data, {
      headers: {
        'Authorization': token
      }
    });
  }
};

// 单独导出API函数，方便直接引用
export const getTeacherList = plasoApi.getTeacherList;
export const getCombinedTeachers = plasoApi.getCombinedTeachers;
export const getDeletedTeachers = plasoApi.getDeletedTeachers;
export const addDeletedTeacher = plasoApi.addDeletedTeacher;
export const deleteDeletedTeacher = plasoApi.deleteDeletedTeacher;
export const getHistoryClasses = plasoApi.getHistoryClasses;
export const getDownloadTasks = plasoApi.getDownloadTasks;
export const createDownloadTask = plasoApi.createDownloadTask;
export const getLiveClasses = plasoApi.getLiveClasses;
export const getClassPlaybackUrl = plasoApi.getClassPlaybackUrl;
export const deleteDownloadTask = plasoApi.deleteDownloadTask;
export const batchDeleteDownloadTasks = plasoApi.batchDeleteDownloadTasks;
export const queryVideoTask = plasoApi.queryVideoTask;
export const getSignedDownloadUrl = plasoApi.getSignedDownloadUrl;
export const getSignedDownloadUrlForDownload = plasoApi.getSignedDownloadUrlForDownload;
export const getSessionList = plasoApi.getSessionList;
export const getClassGroupList = plasoApi.getClassGroupList;
export const getGroupList = plasoApi.getGroupList;
export const getCourseList = plasoApi.getCourseList;
export const updateCourseArchiveInfo = plasoApi.updateCourseArchiveInfo; 